import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import type { Notification } from '@/lib/notifications';

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const handleNewNotification = (event: CustomEvent<Notification>) => {
      const notification = event.detail;
      setNotifications((prev) => [notification, ...prev]);

      // Show toast for high priority notifications
      if (notification.priority === 'high') {
        toast({
          title: notification.title,
          description: notification.message,
          variant: 'destructive',
        });
      }
    };

    window.addEventListener(
      'notification',
      handleNewNotification as EventListener
    );

    return () => {
      window.removeEventListener(
        'notification',
        handleNewNotification as EventListener
      );
    };
  }, [toast]);

  const markAsRead = async (notificationId: string) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  };

  const clearAll = async () => {
    setNotifications([]);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    // Handle navigation or action based on notification type
    switch (notification.type) {
      case 'message':
        // Navigate to chat
        break;
      case 'task_assigned':
        // Navigate to task details
        break;
      // Add more cases as needed
    }
  };

  return {
    notifications,
    isLoading,
    markAsRead,
    clearAll,
    handleNotificationClick,
  };
}